import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from './AuthContext';

const EntitlementsContext = createContext(null);

/** Global entitlements provider — reads /api/entitlements/me whenever the
 * authenticated user changes. Exposes: tier, isAdmin, freePdfUsed,
 * unlockedPropertyIds (Set for fast lookups), propertyCount,
 * canCreateProperty, foundationSnagCap, foundationPropertyCap, plus
 * refresh() for post-mutation invalidation. */
export function EntitlementsProvider({ children }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!user) { setData(null); return; }
        setLoading(true);
        try {
            const res = await axios.get(`${API}/entitlements/me`, { withCredentials: true });
            setData(res.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    const unlockedPropertyIds = new Set(data?.unlocked_property_ids || []);
    const value = {
        ready: !!data,
        loading,
        tier: data?.tier || 'foundation',
        isAdmin: !!data?.is_admin,
        freePdfUsed: !!data?.free_pdf_used,
        unlockedPropertyIds,
        propertyCount: data?.property_count ?? 0,
        canCreateProperty: data?.can_create_property ?? true,
        foundationSnagCap: data?.foundation_snag_cap ?? 10,
        foundationPropertyCap: data?.foundation_property_cap ?? 1,
        foundationAiAnalysisCap: data?.foundation_ai_analysis_cap ?? 3,
        aiAnalysisUsed: data?.ai_analysis_used ?? 0,
        demoPayments: !!data?.demo_payments,
        isPropertyUnlocked: (id) => unlockedPropertyIds.has(id),
        hasUnlimitedAccess: (id) => data?.tier === 'architect' || unlockedPropertyIds.has(id),
        refresh,
    };

    return (
        <EntitlementsContext.Provider value={value}>
            {children}
        </EntitlementsContext.Provider>
    );
}

export const useEntitlements = () => useContext(EntitlementsContext);
